import { Link } from 'react-router-dom';
import { useScrollAnimation, getStaggerProps, animationClasses } from '@/hooks/useScrollAnimation';

export default function CooperationSection() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section ref={ref} className="min-h-[100svh] flex items-center bg-background-100 py-12 md:py-16">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-6xl mx-auto">
          {/* ── 合作購書通路 Header ── */}
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center mb-12 md:mb-16">
            {/* Text — fadeLeft */}
            <div {...getStaggerProps(isVisible, 0, 0, 'fadeLeft')} className="lg:w-1/2 text-center lg:text-left">
              <span className="inline-block text-xs font-medium text-accent-600 tracking-wider uppercase mb-3">
                購書通路
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 tracking-tight">
                財經錢線合作購書通路
              </h2>
              <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-4 max-w-xl mx-auto lg:mx-0">
                財經錢線相關書籍可透過網站已建立的合作購書通路查找。各作品在不同平台的上架狀態、版本、價格及庫存可能不同，實際資訊請以各通路頁面為準。
              </p>
              <p className="text-foreground-400 text-xs leading-relaxed mb-5 max-w-xl mx-auto lg:mx-0">
                書籍價格、折扣、版本、庫存、配送與到貨資訊可能因平台而異，請以各合作購書通路頁面及實際結帳資訊為準。
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Link
                  to="/stores"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  前往通路查找
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </span>
                </Link>
                <a
                  href="https://www.books.com.tw/web/sys_puballb/books/?pubid=sonbookservice"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-accent-500 text-primary-950 rounded-full hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  前往博客來品牌書目
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line text-sm"></i>
                  </span>
                </a>
              </div>
            </div>

            {/* Image — fadeRight */}
            <div {...getStaggerProps(isVisible, 1, 80, 'fadeRight')} className="lg:w-1/2">
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] bg-primary-100">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20bookstore%20or%20library%20interior%20with%20warm%20lighting%2C%20rows%20of%20books%20on%20shelves%2C%20clean%20minimalist%20aesthetic%2C%20deep%20blue%20and%20gold%20accent%20colors%2C%20cream%20and%20warm%20tones%2C%20professional%20editorial%20photography%2C%20calm%20atmosphere&width=800&height=600&seq=cooperation-img&orientation=landscape"
                  alt="財經錢線 Finline"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* ── 正式書目資料庫導流 ── */}
          <div
            {...getStaggerProps(isVisible, 2, 200, 'scaleIn')}
            className="text-center mb-10 md:mb-12"
          >
            <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3 tracking-tight">
              查詢財經錢線完整書目
            </h3>
            <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed mb-5">
              如需確認完整書名、作者、ISBN、出版日期及其他正式出版資訊，請前往崧博出版書籍資料庫查詢。
            </p>
            <a
              href="https://catalog.sonbook.net/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-secondary-500 text-background-50 rounded-full hover:bg-secondary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              查詢完整書目
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-external-link-line text-sm"></i>
              </span>
            </a>
          </div>

          {/* ── 找不到書籍／品牌合作兩欄 ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div
              {...getStaggerProps(isVisible, 3, 300, 'fadeUp')}
              className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-left"
            >
              <div className="w-10 h-10 flex items-center justify-center mb-3 rounded-lg bg-primary-50">
                <i className="ri-search-line text-primary-500 text-lg"></i>
              </div>
              <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                找不到書籍時
              </h4>
              <p className="text-sm text-foreground-500 leading-relaxed">
                若在特定平台找不到財經錢線相關書籍，建議先至崧博出版書籍資料庫確認正式書名、作者與ISBN，再前往其他網站已建立的合作購書通路查找。
              </p>
            </div>

            <div
              {...getStaggerProps(isVisible, 4, 380, 'fadeUp')}
              className="p-6 rounded-xl bg-background-50 border border-background-200/70 text-left"
            >
              <div className="w-10 h-10 flex items-center justify-center mb-3 rounded-lg bg-primary-50">
                <i className="ri-team-line text-primary-500 text-lg"></i>
              </div>
              <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                品牌經銷與合作洽詢
              </h4>
              <p className="text-sm text-foreground-500 leading-relaxed">
                如有財經錢線品牌經銷、通路合作、內容合作、媒體或其他相關合作需求，請透過網站現有的官方聯絡方式提出詢問。品牌經銷與合作方案詳情請直接聯繫洽詢。
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}